import React, {useState, useEffect, useCallback} from 'react';
import {useSelector} from 'react-redux';
import {cloneDeep} from 'lodash';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import OutputText from '@ey/canvascoreservices/OutputText';
import Ellipsis from '../../../Common/Ellipsis/Ellipsis';
import ErrorSummary from '../../../Common/ErrorSummary/ErrorSummary';
import LoadingIndicator from '../../../Common/LoadingIndicator';
import FiltersList from './FiltersList';
import {labels} from '../../../../util/utils';
import Utility from '../../../../util/utilityfunctions';
import Modal from '../../../CoreWrappers/Modal';
const FiltersModal = (props) => {
	const {
		orgChart,
		selectedEntities,
		showModal,
		onModalOk,
		onModalHide,
		onAuxiliaryButtonClick,
		onItemChecked,
		onAllItemsChecked,
		modalTitle,
		primaryButtonLabel,
		secondaryButtonLabel,
		auxiliaryButtonLabel,
		instructionalHeader,
		instructionalText,
		middleSection,
		footerSection,
		isLoading
	} = props;
	const [engagementCount, setEngagementCount] = useState(0);
	const [entities, setEntities] = useState([]);
	const levels = [1, 2, 3, 4];
	const listHeight = middleSection ? '34' : '27';
	const numberFormat = useSelector((store) => store.userPreferences.numberFormat);

	useEffect(() => {
		updateFromOrgChart();
	}, [orgChart, selectedEntities]);

	const updateFromOrgChart = () => {
		if (orgChart) {
			let org = cloneDeep(orgChart);
			for (let i = 0; i < org.length; i++) {
				const exists = selectedEntities.find((c) => c.id === org[i].id);
				org[i].checked = exists ? exists.checked : false;
			}
			updateEntities(org);
		}
	};

	const updateEntities = (newEntities) => {
		setEntities(newEntities);
		setEngagementCount(
			newEntities.reduce((current, item) => {
				if (item.isEnabled) {
					current += item.checked ? item.engagements.length : 0;
				}
				return current;
			}, 0)
		);
	};

	const setCheckStatusToChildrens = (entityList, parentId, checked) => {
		const childrens = entityList.filter((x) => x.parentId === parentId);

		childrens.forEach((x) => {
			if (x.isEnabled) {
				x.checked = checked;
			}
			setCheckStatusToChildrens(entityList, x.id, checked);
		});
	};

	const handlerOnCheckItem = (item, checked) => {
		const _entities = [...entities];

		const entity = _entities.find((x) => x.id === item.id);
		entity.checked = checked;
		setCheckStatusToChildrens(_entities, entity.id, checked);

		updateEntities(_entities);
		if (onItemChecked) {
			onItemChecked(_entities);
		}
	};

	const handlerOnCheckAll = (selectedEntities, checked) => {
		const _entities = [...entities];

		_entities.forEach((x) => {
			const entity = selectedEntities.find((item) => item.id === x.id);
			if (entity && x.isEnabled) {
				x.checked = checked;
			}
		});

		selectedEntities.forEach((z) => {
			setCheckStatusToChildrens(_entities, z.id, checked);
		});

		updateEntities(_entities);

		if (onAllItemsChecked) {
			onAllItemsChecked(_entities);
		}
	};

	const handlerOnOkClick = useCallback(() => {
		if (onModalOk) {
			onModalOk(entities);
		}
	}, [onModalOk, entities]);

	const handlerOnHide = useCallback(() => {
		updateFromOrgChart();
		if (onModalHide) {
			onModalHide();
		}
	}, [onModalHide]);

	const handlerOnAuxiliaryButtonClick = useCallback(() => {
		onAuxiliaryButtonClick && onAuxiliaryButtonClick();
	}, [onAuxiliaryButtonClick]);

	const getFooterSection = () => {
		return (
			<StyledFooterSection>
				{footerSection}
				<div className="selected-engagements">
					<Ellipsis
						lines={2}
						content={`${Utility.getFormattedNumber(numberFormat, engagementCount)} ${labels.engagementsSelected}`}
					/>
				</div>
			</StyledFooterSection>
		);
	};

	const modalProps = {
		show: showModal,
		modalsize: 'large',
		headerText: modalTitle,
		labels: {
			modalConfirmBtnLabel: primaryButtonLabel,
			modalCancelBtnLabel: secondaryButtonLabel,
			modalCloseBtnTitletip: labels.closeLabel,
			modalMiddleBtnLabel: auxiliaryButtonLabel
		},
		hideMiddleButton: !auxiliaryButtonLabel || auxiliaryButtonLabel === '',
		isOkDisabled: isLoading,
		isCancelDisabled: isLoading,
		onOkClick: handlerOnOkClick,
		onClose: handlerOnHide,
		onMiddleButtonClick: handlerOnAuxiliaryButtonClick,
		footerInfo: getFooterSection
	};

	return (
		<StyledFiltersModal>
			<Modal {...modalProps}>
				<ErrorSummary />
				<StyledModalBody>
					<section className="instructional-header">
						<header>
							<OutputText bold>{instructionalHeader}</OutputText>
						</header>
						<p className="instructional-text">{instructionalText}</p>
					</section>

					{isLoading ? (
						<LoadingIndicator show />
					) : (
						<>
							{middleSection}
							<section className="filters-list customScrollbar">
								<section className="filters-wrapper">
									{entities.length > 0 ? (
										levels.map((level) => {
											return (
												<FiltersList
													key={level}
													isGrouping={level > 2}
													level={level}
													allEntities={entities}
													dashboardEntities={entities.filter((e) => {
														return e.entityLevel === level;
													})}
													onCheckItem={handlerOnCheckItem}
													onCheckAll={handlerOnCheckAll}
												/>
											);
										})
									) : (
										<div className="no-data">{labels.thereIsNoDataToDisplay}</div>
									)}
								</section>
							</section>
						</>
					)}
				</StyledModalBody>
			</Modal>
		</StyledFiltersModal>
	);
};

export const EntityShape = {
	checked: PropTypes.bool,
	entityLevel: PropTypes.number,
	id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	isEnabled: PropTypes.bool,
	label: PropTypes.string
};

FiltersModal.propTypes = {
	orgChart: PropTypes.arrayOf(PropTypes.shape(EntityShape)),
	selectedEntities: PropTypes.arrayOf(PropTypes.shape(EntityShape)),
	showModal: PropTypes.bool.isRequired,
	onModalOk: PropTypes.func.isRequired,
	onModalHide: PropTypes.func.isRequired,
	onItemChecked: PropTypes.func,
	onAllItemsChecked: PropTypes.func,
	modalTitle: PropTypes.string,
	primaryButtonLabel: PropTypes.string,
	secondaryButtonLabel: PropTypes.string,
	auxiliaryButtonLabel: PropTypes.string,
	instructionalHeader: PropTypes.string,
	instructionalText: PropTypes.string,
	middleSection: PropTypes.element,
	footerSection: PropTypes.element,
	isLoading: PropTypes.bool
};

export default FiltersModal;

const StyledFiltersModal = styled.section``;

const StyledModalBody = styled.section`
	--gap: 1rem;
	min-height: 0;
	display: flex;
	height: -webkit-fill-available;
	flex-direction: column;
	gap: var(--spacing-x-large);
	.instructional-header {
		display: flex;
		flex-direction: column;
		* {
			margin: 0;
		}
	}
	.filters-list {
		flex: 1;
		display: flex;
		flex-direction: row;
		border: 0.1rem solid ${(props) => props.theme.color[`grey300`]};
		overflow-x: hidden;
		.filters-wrapper {
			display: grid;
			grid-template-columns: 25% 25% 25% 25%;
			width: 100%;
		}
		&:last-child {
			border-right: none;
		}
	}
	.no-data {
		margin: 8rem auto;
		font-weight: 500;
		font-size: 1.3rem;
	}
	.level-3,
	.level-4 {
		border-left: 0.1rem solid ${(props) => props.theme.color[`grey300`]};
		margin-left: -0.1rem;
	}
	.level-4 {
		border-right: none;
	}
`;

const StyledFooterSection = styled.section`
	display: flex;
	.motif-inline-message.motif-inline-message-error.error-text {
		align-items: center;
	}
`;
